import { Bars3Icon } from "@heroicons/react/24/outline";
import { Alerts } from "components/alerts";
import { NavbarPortalProvider } from "components/layout/navbar-portal-context";
import { NgReminders } from "components/ng-reminders/ng-reminders";
import { SidebarLayout } from "components/layout/sidebar-layout";
import { SidebarLayoutCollapsible } from "components/layout/sidebar-layout-collapsible";
import { SidebarLayoutIconsOnly } from "components/layout/sidebar-layout-icons-only";
import { Responsive } from "components/responsive";
import { ReminderProvider } from "contexts/reminder";
import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { designSlice } from "store/slices/design";
import { ApplicationState } from "store/state";
import { AutoReload } from "./auto-reload";
import { NgSidebar } from "./ng-sidebar";
import { NgSidebarCompact } from "./ng-sidebar-compact";
import { NgSidebarCompactIconsOnly } from "./ng-sidebar-compact-icons-only";
import { NgSidebarHamburgler } from "./ng-sidebar-hamburgler";
import { NgSidebarHamburglerTemporary } from "./ng-sidebar-hamburgler-temporary";
import { PreviewUrlHandler } from "./preview-url-handler";
import { UserChangedReload } from "./user-changed-reload";

interface NgContainerProps {
    children: React.ReactNode;
}

export const NgContainer: React.FC<NgContainerProps> = props => {
    const dispatch = useDispatch();
    const sidebarMode = useSelector(
        (state: ApplicationState) => state.design.sidebarMode || "default",
    );
    const compactSidebarState = useSelector(
        (state: ApplicationState) => state.design.compactSidebarState || "expanded",
    );
    const [tempExpanded, setTempExpanded] = useState(false);

    const handleExpandTemporary = useCallback(() => {
        setTempExpanded(true);
    }, []);

    const handlePin = useCallback(() => {
        dispatch(designSlice.actions.setCompactSidebarState("expanded"));
        setTempExpanded(false);
    }, [dispatch]);

    const handleCloseTemporary = useCallback(() => {
        setTempExpanded(false);
    }, []);

    const isHamburglerCollapsed =
        sidebarMode === "hamburgler" && compactSidebarState === "collapsed";
    const isCompactCollapsed =
        sidebarMode === "compact" && compactSidebarState === "collapsed";

    const getSidebar = () => {
        if (isHamburglerCollapsed && tempExpanded) {
            return (
                <NgSidebarHamburglerTemporary
                    onPin={handlePin}
                    onClose={handleCloseTemporary}
                />
            );
        }

        if (isCompactCollapsed) {
            return <NgSidebarCompactIconsOnly />;
        }

        switch (sidebarMode) {
            case "hamburgler":
                return <NgSidebarHamburgler />;
            case "compact":
                return <NgSidebarCompact />;
            case "default":
            default:
                return <NgSidebar />;
        }
    };

    const showHamburgerButton = isHamburglerCollapsed && !tempExpanded;

    return (
        <NavbarPortalProvider>
            <Responsive />
            <AutoReload />
            <UserChangedReload />
            <PreviewUrlHandler />
            <Alerts />
            {showHamburgerButton && (
                <button
                    onClick={handleExpandTemporary}
                    className="fixed top-4 left-4 z-50 p-2 bg-white dark:bg-zinc-900 rounded-lg shadow-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 max-lg:hidden"
                    title="Open sidebar">
                    <Bars3Icon className="w-6 h-6 text-zinc-900 dark:text-white" />
                </button>
            )}
            <ReminderProvider>
                <NgReminders />
                {isHamburglerCollapsed ? (
                    <SidebarLayoutCollapsible
                        sidebar={getSidebar()}
                        navbar={null}
                        showTemporarySidebar={tempExpanded}
                        onCloseTemporarySidebar={handleCloseTemporary}>
                        {props.children}
                    </SidebarLayoutCollapsible>
                ) : isCompactCollapsed ? (
                    <SidebarLayoutIconsOnly
                        sidebar={getSidebar()}
                        mobileSidebar={<NgSidebarCompact />}
                        navbar={null}>
                        {props.children}
                    </SidebarLayoutIconsOnly>
                ) : (
                    <SidebarLayout sidebar={getSidebar()} navbar={null}>
                        {props.children}
                    </SidebarLayout>
                )}
            </ReminderProvider>
        </NavbarPortalProvider>
    );
};
